import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import dayjs from "@shared/dayjs";
import Error from "../../../Reports/components/DashboardShared/Error";
import Button from "../../../shared/v2/Button";
import DatePicker from "../../../shared/v2/DatePicker";
import Dropdown from "../../../shared/v2/Dropdown";
import { ChevronRight } from "../../../shared/v2/Icomoon";
import TextButton from "../../../shared/v2/TextButton";
import useLeadGenActivities from "../../services/useLeadGenActivities";
import Loader from "../Loader";
import LeadGenActivityCard from "./LeadGenActivityCard";
import NewLeadGenActivityModal from "./NewLeadGenActivityModal";
import ViewActivityHistoryModal from "./ViewActivityHistoryModal";
import ViewOpenHouseHistoryModal from "./ViewOpenHouseHistoryModal";

const FROM_FORMAT = "MM/DD/YYYY";
const DATE_FORMAT = "YYYY-MM-DD";

const PERIOD_OPTIONS = [
  { value: "this_week", label: "This Week (Mon - Sun)" },
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "last_week", label: "Last Week (Mon - Sun)" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "this_quarter", label: "This Quarter" },
  { value: "last_quarter", label: "Last Quarter" },
  { value: "this_year", label: "This Year" },
  { value: "last_year", label: "Last Year" },
  { value: "custom", label: "Custom" },
];

const getDate = (position, period) => {
  let date;
  switch (period.value) {
    case "today":
      date = dayjs()[position]("day");
      break;
    case "yesterday":
      date = dayjs().subtract(1, "day")[position]("day");
      break;
    case "this_week":
      date = dayjs()[position]("isoWeek");
      if (date.year() !== dayjs().year()) date = dayjs()[position]("year");
      break;
    case "last_week":
      date = dayjs().subtract(1, "week")[position]("isoWeek");
      if (date.year() !== dayjs().year()) date = dayjs()[position]("year");
      break;
    case "this_month":
      date = dayjs()[position]("month");
      break;
    case "last_month":
      date = dayjs().subtract(1, "month")[position]("month");
      break;
    case "this_quarter":
      date = dayjs()[position]("quarter");
      break;
    case "last_quarter":
      date = dayjs().subtract(1, "quarter")[position]("quarter");
      break;
    case "this_year":
      date = dayjs()[position]("year");
      break;
    case "last_year":
      date = dayjs().subtract(1, "year")[position]("year");
      break;
    default:
      date = position === "startOf" ? dayjs().subtract(7, "day") : dayjs();
      date = date[position]("day");
      if (date.year() !== dayjs().year()) date = dayjs()[position]("year");
  }
  return date.format(FROM_FORMAT);
};

const LeadGenActivities = ({ agentList, ohUsers, userUuids }) => {
  const [startDate, setStartDate] = useState(getDate("startOf", PERIOD_OPTIONS[0]));
  const [endDate, setEndDate] = useState(getDate("endOf", PERIOD_OPTIONS[0]));
  const [period, setPeriod] = useState(PERIOD_OPTIONS[0]);
  const [viewOpenHouseHistoryModal, setViewOpenHouseHistoryModal] = useState(null);
  const [viewHistoryModalGoalType, setViewHistoryModalGoalType] = useState(null);
  const [newLeadGenActivityOpen, setNewLeadGenActivityOpen] = useState(false);
  const [newLeadGenActivityOpenForType, setNewLeadGenActivityOpenForType] = useState(null);
  const { actions, data, error, loading } = useLeadGenActivities({
    period: period.value,
    startDate: dayjs(startDate, FROM_FORMAT).format(DATE_FORMAT),
    endDate: dayjs(endDate, FROM_FORMAT).format(DATE_FORMAT),
    userUuids,
  });

  useEffect(() => {
    setStartDate(getDate("startOf", period));
    setEndDate(getDate("endOf", period));
  }, [period]);

  useEffect(() => {
    if (viewHistoryModalGoalType === null) actions.refreshData();
  }, [viewHistoryModalGoalType]);

  return (
    <div className="tw-flex tw-flex-col tw-gap-[20px] tw-flex-1 tw-relative tw-bg-white tw-min-w-[300px] tw-p-[20px] tw-rounded-[8px]">
      <div className="tw-flex tw-justify-between tw-items-center">
        <h3 className="tw-m-0 tw-text-18d tw-font-normal">Activity Tracking</h3>
        <TextButton
          className="tw-flex tw-items-center tw-text-14d"
          schema="sentence"
          onClick={() => window.location.assign("/settings/goals")}
        >
          Goal Settings <ChevronRight size="s" className="before:tw-align-middle tw-ml-[2px]" />
        </TextButton>
      </div>
      <div className="tw-flex tw-flex-col tw-gap-[12px]">
        <div className="tw-flex tw-flex-col tw-gap-[8px]">
          <Dropdown
            containerClassName="tw-flex-1"
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
          />
          {period?.value === "custom" && (
            <div className="tw-flex tw-gap-[8px]">
              <DatePicker
                containerClassName="tw-flex-1"
                inputProps={{ size: 1 }}
                value={startDate}
                onChange={(value) => {
                  const dayjsStartDate = dayjs(startDate, FROM_FORMAT);
                  const dayjsValue = dayjs(value, FROM_FORMAT);
                  if (dayjsStartDate.year() !== dayjsValue.year()) {
                    setEndDate(dayjs(endDate, FROM_FORMAT).year(dayjsValue.year()).format(FROM_FORMAT));
                  }
                  setStartDate(dayjs(value, FROM_FORMAT).format(FROM_FORMAT));
                }}
              />
              <DatePicker
                containerClassName="tw-flex-1"
                inputProps={{ size: 1 }}
                value={endDate}
                onChange={(value) => {
                  const dayjsEndDate = dayjs(endDate, FROM_FORMAT);
                  const dayjsValue = dayjs(value, FROM_FORMAT);
                  if (dayjsEndDate.year() !== dayjsValue.year()) {
                    setStartDate(dayjs(startDate, FROM_FORMAT).year(dayjsValue.year()).format(FROM_FORMAT));
                  }
                  setEndDate(dayjs(value, FROM_FORMAT).format(FROM_FORMAT));
                }}
              />
            </div>
          )}
        </div>
      </div>
      <div className="tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-gray-5" />
      <div className="tw-flex tw-flex-col tw-gap-[20px]">
        <span className="tw-text-14d tw-text-neutral-gray-50 tw-font-semibold">Brivity Activities</span>
        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-x-[20px] tw-gap-y-[36px]">
          {data?.goalTypes
            .filter(({ goalSection }) => goalSection === "lead_interaction")
            .map((goalType) => (
              <LeadGenActivityCard
                key={goalType.id}
                label={goalType.displayName}
                goalValue={data.goals[goalType.id]?.goalValue || 0}
                value={data.brivityActivities[goalType.id] || 0}
                onViewHistory={
                  goalType.name === "open_houses"
                    ? () =>
                        setViewOpenHouseHistoryModal({
                          goalType,
                          goalValue: data.brivityActivities[goalType.id],
                        })
                    : null
                }
              />
            ))}
        </div>
        <div className="tw-border-solid tw-border-x-0 tw-border-t-0 tw-border-gray-5" />
        <div className="tw-flex tw-justify-between tw-items-center">
          <span className="tw-text-14d tw-text-neutral-gray-50 tw-font-semibold">Logged Activities</span>
          <Button schema="secondary" onClick={() => setNewLeadGenActivityOpen(true)}>
            + Log
          </Button>
        </div>
        <div className="tw-flex tw-flex-wrap tw-justify-center tw-gap-x-[20px] tw-gap-y-[36px] tw-pb-[20px]">
          {data?.goalTypes
            .filter(({ goalSection }) => goalSection === "activity")
            .map((goalType) => (
              <LeadGenActivityCard
                key={goalType.id}
                isButton
                label={goalType.displayName}
                goalValue={data.goals[goalType.id]?.goalValue || 0}
                onClick={() => setNewLeadGenActivityOpenForType(String(goalType.id))}
                onViewHistory={() => setViewHistoryModalGoalType(goalType)}
                value={data.loggedActivities[goalType.id] || 0}
              />
            ))}
        </div>
      </div>

      <NewLeadGenActivityModal
        agentList={agentList}
        userUuids={userUuids}
        goalTypes={(data?.goalTypes || []).filter(({ goalSection }) => goalSection === "activity")}
        open={newLeadGenActivityOpen}
        openForType={newLeadGenActivityOpenForType}
        onClose={() => {
          setNewLeadGenActivityOpen(false);
          setNewLeadGenActivityOpenForType(null);
        }}
        onSubmit={actions.createAgentActivity}
      />

      <ViewActivityHistoryModal
        userUuids={userUuids}
        goalType={viewHistoryModalGoalType}
        onClose={() => setViewHistoryModalGoalType(null)}
      />

      <ViewOpenHouseHistoryModal
        ohUsers={ohUsers}
        endDate={dayjs(endDate, FROM_FORMAT).format(DATE_FORMAT)}
        goalType={viewOpenHouseHistoryModal?.goalType}
        onClose={() => setViewOpenHouseHistoryModal(false)}
        startDate={dayjs(startDate, FROM_FORMAT).format(DATE_FORMAT)}
        total={viewOpenHouseHistoryModal?.goalValue}
        userUuids={userUuids}
        onSubmit={actions.createAgentActivity}
      />

      <Error className="tw-mt-[20px]" show={Boolean(error)} onClose={() => actions.setError(null)} />
      {loading && <Loader />}
    </div>
  );
};

LeadGenActivities.propTypes = {
  agentList: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  ohUsers: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  userUuids: PropTypes.arrayOf(PropTypes.string).isRequired,
};

export default LeadGenActivities;
